import { useState } from 'react'
import cx from 'classnames'
import { WarehouseDirectory, WarehouseDirectoryItem } from '@/console/api/types'
import { setWarehouseActive } from '@/console/data/warehousePolicy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Note } from '@/console/ui/Note'
import { describeMissing } from './directory'
import { useWriteAction, WriteCallbacks } from './usePolicySave'
import { StatusTags } from './WarehouseTags'
import styles from './Warehouse.module.css'

/**
 * LOGISPRD-15602 — 오른쪽 상단 창고 헤더(코드 · 이름 · 태그 · 활성 전환).
 *
 * 활성 전환 버튼은 admin 모드(`get-warehouse-master-list` 성공)에서만 보인다 — 활성 목록 폴백은 FF 가 꺼진 상태라 전환 API 도 404 다.
 * 비활성 + 속성 미완이면 `활성화` 를 잠그고 누락 칩을 보인다(BE 도 400 으로 거절하지만 누를 이유가 없게 미리 막는다).
 * 부모가 `key={code}` 로 리마운트해 창고를 바꾸면 전환 결과·오류가 남지 않는다.
 */
export const WarehouseHeader = ({
  code,
  item,
  mode,
  locked,
  ...callbacks
}: WriteCallbacks & {
  /** 다른 카드가 저장 중이면 true — 전환 버튼도 잠근다. */
  locked: boolean
  code: string
  item: WarehouseDirectoryItem | undefined
  mode: WarehouseDirectory['mode'] | null
}): JSX.Element => {
  // 확인창을 연 시점의 의도(true=활성화, false=비활성화)를 고정한다 — 열린 사이 목록이 바뀌어도 라벨과 전송값이 같다.
  const [intent, setIntent] = useState<boolean | null>(null)
  const [unchanged, setUnchanged] = useState(false)
  const { saving, error, run } = useWriteAction(callbacks)

  const canToggle = mode === 'admin' && item !== undefined
  const activating = item !== undefined && !item.active
  const missing = (item?.missingAttributes ?? []).map(describeMissing)
  const activationBlocked = activating && item?.attributesComplete === false

  // 새 전환을 시작하면 직전 결과("이미 그 상태예요")는 지난 시도의 것이라 지운다 — 열고 취소해도 남지 않게.
  const openIntent = (target: boolean): void => {
    setUnchanged(false)
    setIntent(target)
  }

  const confirm = (): void => {
    const target = intent
    setIntent(null)
    if (!item || target === null) {
      return
    }
    setUnchanged(false)
    run(
      () => setWarehouseActive(code, target),
      (result) => {
        setUnchanged(!result.changed)
        callbacks.onSaved()
      },
    )
  }

  const editable = missing.filter((m) => m.editableHere)
  const notEditable = missing.filter((m) => !m.editableHere)

  return (
    <div className={styles.whHead}>
      <div className={styles.whHeadTop}>
        <span className={cx(styles.dot, item?.active ? styles.dotOn : styles.dotOff)} aria-hidden="true" />
        <div className={styles.whHeadTitle}>
          <h3 className={styles.whCode}>{code}</h3>
          <span className={item?.warehouseName ? styles.whName : styles.whNameMissing}>{item?.warehouseName ?? '이름 없음'}</span>
        </div>
        {canToggle ? (
          <div className={styles.whHeadAction}>
            {activating ? (
              <Button size="sm" disabled={saving || locked || activationBlocked} onClick={() => openIntent(true)}>
                {saving ? '전환 중…' : '활성화'}
              </Button>
            ) : (
              // 콘솔 `Button` 은 solid · line · ghost 만 있고 className 을 받지 않는다 — 위험(빨강) 테두리는 이 버튼만 써서 원시 button 에 둔다.
              <button type="button" className={styles.dangerLine} disabled={saving || locked} onClick={() => openIntent(false)}>
                {saving ? '전환 중…' : '비활성화'}
              </button>
            )}
          </div>
        ) : null}
      </div>
      {item ? (
        <div className={styles.whHeadTags}>
          <StatusTags item={item} showLes={mode === 'admin'} />
        </div>
      ) : null}

      {activationBlocked ? (
        <div className={styles.whHeadBlock}>
          <Callout tone="warn">
            <b>활성화하려면 아래 속성을 채워야 해요:</b>
            {editable.length > 0 ? (
              <div className={styles.missingGroup}>
                <span className={styles.missingCaption}>아래 카드에서 채울 수 있어요</span>
                <span className={styles.chips}>
                  {editable.map((m) => (
                    <span key={m.key} className={styles.chip}>
                      {m.label}
                    </span>
                  ))}
                </span>
              </div>
            ) : null}
            {notEditable.length > 0 ? (
              <div className={styles.missingGroup}>
                <span className={styles.missingCaption}>이 화면에서 편집할 수 없어요</span>
                <span className={styles.chips}>
                  {notEditable.map((m) => (
                    <span key={m.key} className={cx(styles.chip, styles.chipLocked)}>
                      {m.label}
                    </span>
                  ))}
                </span>
              </div>
            ) : null}
          </Callout>
        </div>
      ) : null}

      {unchanged ? <Note>이미 그 상태예요.</Note> : null}
      {error ? (
        <div className={styles.whHeadBlock}>
          <Callout tone="crit">{error.message}</Callout>
        </div>
      ) : null}

      <ConfirmDialog
        open={intent !== null}
        title={intent ? '창고 활성화' : '창고 비활성화'}
        body={
          intent ? (
            <p className={styles.dialogLead}>
              <b>{code}</b> 를 활성화할까요?
            </p>
          ) : (
            <p className={styles.dialogLead}>
              <b>{code}</b> 를 비활성화할까요? 비활성 창고는 활성 창고 조회(get-all-warehouses)에서 빠져요.
            </p>
          )
        }
        confirmLabel={intent ? '활성화' : '비활성화'}
        danger={!intent}
        onConfirm={confirm}
        onCancel={() => setIntent(null)}
      />
    </div>
  )
}
